// src/features/dashboard/admin/UserForm.jsx
import React, { useState } from 'react';
import { SUBJECTS } from '../../../data/subjects';
import { GRADE_LEVELS } from '../../../data/gradeLevels';
import { CLASS_STREAMS } from '../../../data/classStreams';
import { splitGrade } from '../../../utils/gradeUtils';

const UserForm = ({
  user,
  defaultValues = {},
  onSubmit,
  onCancel,
  isSuperAdmin = false,
  isOrganisationReadOnly = true,
  teacherOptions = [], // array of teacher emails/names
}) => {
  const initialGrade = user?.grade || defaultValues.grade || '';
  const { gradeLevel: initialLevel, stream: initialStream } = splitGrade(initialGrade);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    email: user?.email || '',
    role: user?.role || 'Student',
    organisationId: user?.organisationId || defaultValues.organisationId || '',
    school: user?.school || defaultValues.school || '',
    province: user?.province || defaultValues.province || '',
    district: user?.district || defaultValues.district || '',
    circuit: user?.circuit || defaultValues.circuit || '',
    gradeLevel: initialLevel || '',
    stream: initialStream || '',
    teacher: user?.teacher || '',
    subject: user?.subject || '',
    studentType: user?.studentType || 'group',
    gender: user?.gender || '',
    status: user?.status || 'active',
    googleClassroomLink: user?.googleClassroomLink || '',
    schedule: user?.schedule || [{ day: '', startTime: '', duration: '' }],
    termData: user?.termData || [{ term: '', termStart: '', termEnd: '' }],
    zoomLink: user?.zoomLink || '',
    zoomPassword: user?.zoomPassword || '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData({ ...formData, schedule: newSchedule });
  };

  const addSession = () => {
    setFormData({
      ...formData,
      schedule: [...formData.schedule, { day: '', startTime: '', duration: '' }]
    });
  };

  const removeSession = (index) => {
    if (formData.schedule.length <= 1) return;
    const newSchedule = formData.schedule.filter((_, i) => i !== index);
    setFormData({ ...formData, schedule: newSchedule });
  };

  // ---- Term handlers ----
  const addTermRow = () => {
    setFormData({
      ...formData,
      termData: [...formData.termData, { term: '', termStart: '', termEnd: '' }]
    });
  };

  const removeTermRow = (index) => {
    if (formData.termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = formData.termData.filter((_, i) => i !== index);
    setFormData({ ...formData, termData: newTermData });
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...formData.termData];
    newTermData[index][field] = value;
    setFormData({ ...formData, termData: newTermData });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const combinedGrade = `${formData.gradeLevel}${formData.stream}`.trim();

    // ✅ Filter out empty term rows
    const validTerms = formData.termData.filter(t => t.term && t.termStart && t.termEnd);

    const submission = {
      ...formData,
      grade: combinedGrade,
      role: formData.role,
      termData: validTerms, // ✅ Include termData
    };
    delete submission.gradeLevel;
    delete submission.stream;
    onSubmit(submission);
  };

  // Role options: only superadmin can create superadmin
  const roleOptions = () => {
    const roles = [
      { value: 'Student', label: 'Student' },
      { value: 'Teacher', label: 'Teacher' },
    ];
    if (isSuperAdmin) {
      roles.push({ value: 'superadmin', label: 'Super Admin' });
      roles.push({ value: 'admin', label: 'Admin' });
    } else {
      roles.push({ value: 'admin', label: 'Admin' });
    }
    return roles;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Basic info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Surname *</label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Role *</label>
        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
          required
        >
          {roleOptions().map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* 👇 Status Field */}
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2"
        >
          <option value="active">Active (Read + Write)</option>
          <option value="partial">Partial (Read Only)</option>
          <option value="inactive">Inactive (No Access)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Active users can edit spreadsheets. Partial users can only view. Inactive users have no access.
        </p>
      </div>

      {/* Organisation Details – inherited and read-only unless superadmin */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-2">Organisation Details</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Organisation ID</label>
            <input
              type="text"
              name="organisationId"
              value={formData.organisationId}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${isOrganisationReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              readOnly={isOrganisationReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">School</label>
            <input
              type="text"
              name="school"
              value={formData.school}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${isOrganisationReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              readOnly={isOrganisationReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Province</label>
            <input
              type="text"
              name="province"
              value={formData.province}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${isOrganisationReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              readOnly={isOrganisationReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">District</label>
            <input
              type="text"
              name="district"
              value={formData.district}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${isOrganisationReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              readOnly={isOrganisationReadOnly}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Circuit</label>
            <input
              type="text"
              name="circuit"
              value={formData.circuit}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${isOrganisationReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              readOnly={isOrganisationReadOnly}
            />
          </div>
        </div>
      </div>

      {/* Student-specific fields */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-2">Student Details</h4>
        <div className="grid grid-cols-2 gap-4">

          {/* Admission Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Admission Type</label>
            <select
              name="studentType"
              value={formData.studentType}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="group">Group</option>
              <option value="tutor">Tutor</option>
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium mb-1">Gender</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>


          {/* Grade Level + Stream */}
          <div>
            <label className="block text-sm font-medium mb-1">Grade Level</label>
            <select
              name="gradeLevel"
              value={formData.gradeLevel}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select Grade</option>
              {GRADE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          {/* ✅ Only show Stream if the user is a GROUP student */}
          {formData.studentType !== 'tutor' && (
            <div>
              <label className="block text-sm font-medium mb-1">Stream</label>
              <select
                name="stream"
                value={formData.stream}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">None</option>
                {CLASS_STREAMS.map(stream => (
                  <option key={stream} value={stream}>{stream}</option>
                ))}
              </select>
            </div>
          )}

          {/* Teacher (email) – dropdown with datalist */}
          <div>
            <label className="block text-sm font-medium mb-1">Teacher</label>
            <input
              type="text"
              name="teacher"
              list="teacher-options"
              value={formData.teacher}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Type or select teacher name"
            />
            <datalist id="teacher-options">
              {teacherOptions.map(t => (
                <option key={t.email} value={t.email}>{t.display}</option>
              ))}
            </datalist>
          </div>

          {/* Subject – searchable dropdown */}
          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              list="subject-options"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Type or select subject"
            />
            <datalist id="subject-options">
              {SUBJECTS.map(subj => (
                <option key={subj} value={subj} />
              ))}
            </datalist>
          </div>


        </div>
      </div>

      {/* Class Details (Zoom, Classroom, Schedule) */}
      <div className="border-t pt-4">
        <h4 className="font-medium mb-2">Class Details</h4>
        <div>
          <label className="block text-sm font-medium mb-1">Google Classroom Link</label>
          <input
            type="text"
            name="googleClassroomLink"
            value={formData.googleClassroomLink}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div className="mt-2">
          <label className="block text-sm font-medium mb-1">Zoom Link</label>
          <input
            type="text"
            name="zoomLink"
            value={formData.zoomLink}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div className="mt-2">
          <label className="block text-sm font-medium mb-1">Zoom Password</label>
          <input
            type="text"
            name="zoomPassword"
            value={formData.zoomPassword}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        {/* ===== TERM DATA SECTION ===== */}
        <div className="mt-4 border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Term Dates</h4>
            <button
              type="button"
              onClick={addTermRow}
              className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
            >
              + Add Term
            </button>
          </div>
          {formData.termData.map((term, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Term Name</label>
                <input
                  type="text"
                  value={term.term || ''}
                  onChange={e => updateTerm(idx, 'term', e.target.value)}
                  placeholder="e.g., Term 1"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Date</label>
                <input
                  type="date"
                  value={term.termStart || ''}
                  onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">End Date</label>
                  <input
                    type="date"
                    value={term.termEnd || ''}
                    onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {formData.termData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTermRow(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove term"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.termData.length === 0 && (
            <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Terms set here will be stored in the student's schedule with TERM_START and TERM_END markers.
          </p>
        </div>

        {/* Schedule */}
        <div className="mt-4 border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Sessions</h4>
            <button
              type="button"
              onClick={addSession}
              className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              + Add Session
            </button>
          </div>
          {formData.schedule.map((session, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Day</label>
                <input
                  type="text"
                  value={session.day || ''}
                  onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                  placeholder="e.g., Monday"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Time</label>
                <input
                  type="text"
                  value={session.startTime || ''}
                  onChange={e => handleScheduleChange(idx, 'startTime', e.target.value)}
                  placeholder="e.g., 09:00"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">Duration (min)</label>
                  <input
                    type="number"
                    value={session.duration || ''}
                    onChange={e => handleScheduleChange(idx, 'duration', e.target.value)}
                    placeholder="60"
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {formData.schedule.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove session"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.schedule.length === 0 && (
            <p className="text-sm text-gray-500">No sessions added. Click "+ Add Session" to add one.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Save
        </button>
      </div>
    </form>
  );
};

export default UserForm;